import { useState } from 'react';
import {
  Button,
  Flex,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useTheme,
} from '@chakra-ui/react';
import { SocialProfile } from 'src/api/v1-api';
import MobileRowLabel from '../mobile-row-label';
import { getSocialProfileIcon } from 'src/utils/socialProfileIcons';
import { MdAddCircle, MdEdit, MdMoreVert, MdRemoveCircle } from 'react-icons/md';
import { FaTrash } from 'react-icons/fa';

interface SocialProfilesTableProps {
  socialProfiles: SocialProfile[];
  editSocialProfile: (socialProfile: Partial<SocialProfile>) => void;
  onUpdateSocialProfile: (socialProfile: SocialProfile) => void;
  onDeleteSocialProfile: (socialProfile: SocialProfile) => void;
}

const SocialProfilesTable = ({
  socialProfiles,
  editSocialProfile,
  onUpdateSocialProfile,
  onDeleteSocialProfile,
}: SocialProfilesTableProps) => {
  const theme = useTheme();

  return (
    <Flex direction="column" width="100%" className="social-profiles-table">
      <Flex
        py={4}
        w="100%"
        borderBottom="1px solid"
        borderColor="gray.200"
        gap={4}
        display={{ base: 'none', lg: 'flex' }}
        color="gray.600"
        textTransform="uppercase"
        fontSize="xs"
        fontWeight="bold"
      >
        <Flex w="15%" pl={4}>
          Network
        </Flex>
        <Flex w="25%">Username</Flex>
        <Flex w="55%">Link</Flex>
        <Flex w="5%" justifyContent="end" pr={4}>
          Actions
        </Flex>
      </Flex>
      {socialProfiles.map((profile, index) => (
        <Flex
          key={profile.id}
          flexDirection={{ base: 'column', lg: 'row' }}
          w="100%"
          borderBottom="1px solid"
          borderColor="gray.200"
          py={{ base: 6, lg: 2 }}
          gap={4}
          alignItems={{ base: 'flex-start', lg: 'center' }}
        >
          <Flex w={{ lg: '15%' }} pl={{ base: 0, lg: 4 }} direction="column">
            <MobileRowLabel label="Network" />
            {getSocialProfileIcon({
              networkName: profile.network,
              fillColor: theme.colors.mainText,
            })}
          </Flex>
          <Flex w={{ lg: '25%' }} direction="column">
            <MobileRowLabel label="Username" />
            <Text noOfLines={1}>{profile.username}</Text>
          </Flex>
          <Flex w={{ lg: '55%' }} direction="column">
            <MobileRowLabel label="Link" />
            <Text noOfLines={1}>
              <Link href={profile.url} target="_blank">
                {profile.url}
              </Link>
            </Text>
          </Flex>
          <Flex display={{ base: 'flex', lg: 'none' }} direction="row" gap={2} flexWrap={'wrap'}>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() => editSocialProfile(profile)}
              leftIcon={<MdEdit />}
            >
              Edit
            </Button>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() => onDeleteSocialProfile(profile)}
              leftIcon={<FaTrash />}
            >
              Remove
            </Button>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() =>
                onUpdateSocialProfile({
                  ...profile,
                  show_on_header_menu: !profile.show_on_header_menu,
                })
              }
              leftIcon={profile.show_on_header_menu ? <MdRemoveCircle /> : <MdAddCircle />}
            >
              {profile.show_on_header_menu ? 'Remove from' : 'Add to'} header
            </Button>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() =>
                onUpdateSocialProfile({
                  ...profile,
                  show_on_footer_menu: !profile.show_on_footer_menu,
                })
              }
              leftIcon={profile.show_on_footer_menu ? <MdRemoveCircle /> : <MdAddCircle />}
            >
              {profile.show_on_footer_menu ? 'Remove from' : 'Add to'} footer
            </Button>
          </Flex>
          <Flex w="5%" justifyContent="end" pr={4} display={{ base: 'none', lg: 'flex' }}>
            <Menu>
              <MenuButton as={IconButton} aria-label="Actions" icon={<MdMoreVert />} />
              <MenuList>
                <MenuItem icon={<MdEdit />} onClick={() => editSocialProfile(profile)}>
                  Edit
                </MenuItem>
                <MenuItem icon={<FaTrash />} onClick={() => onDeleteSocialProfile(profile)}>
                  Remove
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    onUpdateSocialProfile({
                      ...profile,
                      show_on_header_menu: !profile.show_on_header_menu,
                    })
                  }
                  icon={profile.show_on_header_menu ? <MdRemoveCircle /> : <MdAddCircle />}
                >
                  {profile.show_on_header_menu ? 'Remove from' : 'Add to'} header
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    onUpdateSocialProfile({
                      ...profile,
                      show_on_footer_menu: !profile.show_on_footer_menu,
                    })
                  }
                  icon={profile.show_on_footer_menu ? <MdRemoveCircle /> : <MdAddCircle />}
                >
                  {profile.show_on_footer_menu ? 'Remove from' : 'Add to'} footer
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
};

export default SocialProfilesTable;
